import React, { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CmsProvider } from './context/CmsContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import About from './pages/About';
import MediaMentions from './pages/MediaMentions';
import FAQ from './pages/FAQ';
import GalleryPage from './pages/GalleryPage';
import BeforeAfterPage from './pages/BeforeAfterPage';
import CasesPage from './pages/CasesPage';
import CaseDetail from './pages/CaseDetail';
import Contact from './pages/Contact';
import Prices from './pages/Prices';
import CityLanding from './pages/CityLanding';
import NotFound from './pages/NotFound';
import Forbidden from './pages/Forbidden';

// ScrollToTop component to ensure page transitions always start at the top and trigger SPA tracking
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    // Trigger Meta Pixel PageView on route change
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }

    // Trigger GTM virtual pageview on dataLayer for SPA page tracking
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        event_name: 'page_view',
        page_path: pathname,
        page_location: window.location.href,
        page_title: document.title
      });
    }
  }, [pathname]);

  return null;
}

// Global listener for clicks on phone number and email links (GTM tracking)
function ContactClickTracker() {
  useEffect(() => {
    const handleContactClick = (event) => {
      // 1. Phone number clicks
      const telLink = event.target.closest('a[href^="tel:"]');
      if (telLink) {
        const rawHref = telLink.getAttribute('href') || '';
        const phoneNumber = rawHref.replace('tel:', '').trim();

        // Push to GTM dataLayer
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'click_to_call',
            phone_number: phoneNumber,
            event_category: 'Kontakt',
            event_label: `Opkald: ${phoneNumber}`
          });
        }
      }

      // 2. Email clicks
      const mailtoLink = event.target.closest('a[href^="mailto:"]');
      if (mailtoLink) {
        const rawHref = mailtoLink.getAttribute('href') || '';
        const emailAddress = rawHref.replace('mailto:', '').split('?')[0].trim();

        // Push to GTM dataLayer
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'click_to_email',
            email_address: emailAddress,
            event_category: 'Kontakt',
            event_label: `Email: ${emailAddress}`
          });
        }
      }
    };

    document.addEventListener('click', handleContactClick);
    return () => {
      document.removeEventListener('click', handleContactClick);
    };
  }, []);

  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <CmsProvider>
        <div className="relative min-h-screen bg-white text-zinc-800 antialiased flex flex-col justify-between">
          {/* Scroll Restoration */}
          <ScrollToTop />
          {/* Contact (Phone & Email) Click Conversion Tracking */}
          <ContactClickTracker />

          {/* Global Navigation */}
          <Navbar />

          {/* Main Content Area */}
          <main className="flex-grow">
            <Routes>
              {/* Primary Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/ydelser" element={<Services />} />
              <Route path="/ydelser/:slug" element={<ServiceDetail />} />
              <Route path="/om-os" element={<About />} />
              <Route path="/medieomtale" element={<MediaMentions />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/galleri" element={<GalleryPage />} />
              <Route path="/foer-efter" element={<BeforeAfterPage />} />
              <Route path="/cases" element={<CasesPage />} />
              <Route path="/cases/:id" element={<CaseDetail />} />
              <Route path="/priser" element={<Prices />} />
              <Route path="/kontakt" element={<Contact />} />

              {/* Skjulte lokale SEO-landingssider (kun de 3 officielle URL'er) */}
              <Route path="/laserrensning-koebenhavn" element={<CityLanding cityKey="koebenhavn" />} />
              <Route path="/laserrensning-aarhus" element={<CityLanding cityKey="aarhus" />} />
              <Route path="/laserrensning-odense" element={<CityLanding cityKey="odense" />} />

              {/* Error Pages */}
              <Route path="/403" element={<Forbidden />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>

          {/* Global Footer */}
          <Footer />
        </div>
      </CmsProvider>
    </HelmetProvider>
  );
}
